import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../LanguageContext";
import { Reveal, Overline } from "./Reveal";
import { optImg } from "../lib/img";

const TiltCard = ({ item, index, onOpen, lang }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 150, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 150, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  const c = item[lang];

  return (
    <motion.button
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={() => onOpen(item.id)}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.1, ease: [0.19, 1, 0.22, 1] }}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
      className="group relative text-left bg-ink overflow-hidden"
    >
      <div className="relative aspect-[4/5] overflow-hidden" style={{ transform: "translateZ(0)" }}>
        <img src={optImg(item.image, 800)} alt={c.name} loading="lazy" decoding="async"
          className="h-full w-full object-cover opacity-85 transition-all duration-700 ease-out group-hover:scale-110 group-hover:opacity-100" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-transparent" />
        <span className="absolute top-5 left-6 font-serif text-white/50 text-lg">{item.number}</span>
        <span className="absolute top-5 right-6 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 text-gold-light">
          <ArrowUpRight size={22} strokeWidth={1.3} />
        </span>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7" style={{ transform: "translateZ(40px)" }}>
        <p className="overline text-gold-light mb-2">{c.short}</p>
        <h3 className="font-serif text-white text-2xl md:text-3xl font-light leading-tight">{c.name}</h3>
        <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-500 ease-out">
          <p className="overflow-hidden text-white/70 text-sm font-light mt-0 group-hover:mt-3 leading-relaxed">{c.desc}</p>
        </div>
      </div>
    </motion.button>
  );
};

const Collections = ({ onOpen }) => {
  const { t, lang, collections } = useLang();
  const COLLECTIONS = collections;
  return (
    <section id="products" className="bg-paper-warm py-24 md:py-36">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <Reveal><Overline>{t.collectionsSection.overline}</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-ink font-light leading-[1.05] text-4xl md:text-6xl mt-6 max-w-2xl">
                {t.collectionsSection.title}
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
          {COLLECTIONS.map((item, i) => (
            <TiltCard key={item.id} item={item} index={i} onOpen={onOpen} lang={lang} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Collections;
